import _ from 'lodash';
import { AppFilters, UserFilters } from '../typing/local-storage';
import { FiltersConfiguration } from '../typing/main';
import { Filters } from '../typing/translations';
import {
  ColorFilterItem,
  Dealer,
  DealerFilterItem,
  FilterItem,
  UsedInventoryData,
} from '../typing/used-inventory';
import {
  DEFAULT_FUEL_TYPE,
  DEFAULT_MILEAGE_FILTER,
  DEFAULT_PRICE_FILTER,
} from './app-constants';
import { formatNumberWithCommas } from './general';

const FeatureFilterKeys = {
  models: 'modelName',
  years: 'modelYear',
  fuelType: 'fuelType',
  transmission: 'transmissionType',
  interiorColor: 'interiorColor',
  exteriorColor: 'exteriorColor',
};

export const FeatureResultsKeys = {
  dealers: 'dealers',
  models: 'modelName',
  years: 'modelYear',
  fuelType: 'fuelType',
  transmission: 'transmissionType',
  interiorColor: 'interiorColor',
  exteriorColor: 'exteriorColor',
  OnlineBuyingKey: 'isOnlineShopping',
};

const FeatureUserKeys = {
  dealers: 'dealers',
  models: 'modelName',
  years: 'modelYear',
  fuelType: 'fuelType',
  transmission: 'transmissionType',
  interiorColor: 'interiorColor',
  exteriorColor: 'exteriorColor',
};

export const isAttributeChecked = (filter?: string[] | null, id?: string) => {
  const attribute = filter?.find((key) => _.toString(key) === _.toString(id));
  return !_.isEmpty(_.toString(attribute));
};

export const getAttributeStock = (filters?: any[], id?: string) => {
  const attribute = filters?.find(
    (filter) => _.toString(filter.key) === _.toString(id),
  );
  return (!_.isEmpty(attribute) && attribute.value) || 0;
};
export const mergeFiltersData = (
  allFilters: any,
  currentFilters: UserFilters,
  resultFilters: UsedInventoryData,
  filtersConfig: FiltersConfiguration,
  filtersTranslation: Filters,
): AppFilters => {
  const dealers: DealerFilterItem[] = [];
  const models: FilterItem[] = [];
  const years: FilterItem[] = [];
  const fuelType: FilterItem[] = [];
  const transmissionType: FilterItem[] = [];
  const exteriorColor: ColorFilterItem[] = [];
  const interiorColor: ColorFilterItem[] = [];
  const appliedFilters: FilterItem[] = [];
  /**
   * Processing Dealers filter
   */
  resultFilters.dealers?.map((dealer: Dealer) => {
    if (!_.isEmpty(dealer)) {
      const tempItem = {
        key: dealer.dealerid,
        value: dealer.name,
        distance: dealer.distance,
        isChecked: isAttributeChecked(
          currentFilters.filters &&
          currentFilters.filters[FeatureResultsKeys.dealers],
          dealer.dealerid,
        ),
        stock: getAttributeStock(
          resultFilters.filter?.filterAttributes?.dealers,
          dealer.dealerid,
        ),
        type: FeatureUserKeys.dealers,
      };
      tempItem.isChecked && appliedFilters.push(tempItem);
      dealers.push(tempItem);
    }
  });

  // Processing Models
  allFilters[FeatureFilterKeys.models].map((modelName: string) => {
    const tempItem = {
      key: modelName,
      value: modelName,
      isChecked: isAttributeChecked(
        currentFilters.filters &&
        currentFilters.filters[FeatureResultsKeys.models],
        modelName,
      ),
      stock: getAttributeStock(
        resultFilters.filter?.filterAttributes?.models,
        modelName,
      ),
      type: FeatureUserKeys.models,
    };
    tempItem.isChecked && appliedFilters.push(tempItem);
    models.push(tempItem);
  });

  // Processing years
  allFilters[FeatureFilterKeys.years].map((year: string) => {
    const tempItem = {
      key: year,
      value: year,
      isChecked: isAttributeChecked(
        currentFilters.filters &&
        currentFilters.filters[FeatureResultsKeys.years],
        year,
      ),
      stock: getAttributeStock(
        resultFilters.filter?.filterAttributes?.modelYear,
        year,
      ),
      type: FeatureUserKeys.years,
    };
    tempItem.isChecked && appliedFilters.push(tempItem);
    years.push(tempItem);
  });

  // Processing fuel Type
  allFilters[FeatureFilterKeys.fuelType].map((fuel: string) => {
    const tempItem = {
      key: fuel,
      value:
        filtersConfig.fuelType.typeMap[fuel || ''] ||
        DEFAULT_FUEL_TYPE.typeMap[''],
      isChecked: isAttributeChecked(
        currentFilters.filters &&
        currentFilters.filters[FeatureResultsKeys.fuelType],
        fuel,
      ),
      stock: getAttributeStock(
        resultFilters.filter?.filterAttributes?.fuelType,
        fuel,
      ),
      type: FeatureUserKeys.fuelType,
    };
    tempItem.isChecked && appliedFilters.push(tempItem);
    fuelType.push(tempItem);
  });

  // Processing Transmission
  allFilters[FeatureFilterKeys.transmission].map((transmission: string) => {
    const tempItem = {
      key: transmission,
      value: transmission,
      isChecked: isAttributeChecked(
        currentFilters.filters &&
        currentFilters.filters[FeatureResultsKeys.transmission],
        transmission,
      ),
      stock: getAttributeStock(
        resultFilters.filter?.filterAttributes?.transmissionType,
        transmission,
      ),
      type: FeatureUserKeys.transmission,
    };
    tempItem.isChecked && appliedFilters.push(tempItem);
    transmissionType.push(tempItem);
  });

  // Processing Interior colors
  allFilters[FeatureFilterKeys.interiorColor].map((interior: string) => {
    const tempItem = {
      key: interior,
      value: interior,
      isChecked: isAttributeChecked(
        currentFilters.filters &&
        currentFilters.filters[FeatureResultsKeys.interiorColor],
        interior,
      ),
      stock: getAttributeStock(
        resultFilters.filter?.filterAttributes?.interiorColor,
        interior,
      ),
      type: FeatureUserKeys.interiorColor,
      colorHex: filtersConfig.interior.colorMap[interior] || '',
    };
    tempItem.isChecked && appliedFilters.push(tempItem);
    interiorColor.push(tempItem);
  });

  // Processing exterior Color
  allFilters[FeatureFilterKeys.exteriorColor].map((exterior: string) => {
    const tempItem = {
      key: exterior,
      value: exterior,
      isChecked: isAttributeChecked(
        currentFilters.filters &&
        currentFilters.filters[FeatureResultsKeys.exteriorColor],
        exterior,
      ),
      stock: getAttributeStock(
        resultFilters.filter?.filterAttributes?.exteriorColor,
        exterior,
      ),
      type: FeatureUserKeys.exteriorColor,
      colorHex: filtersConfig.exterior.colorMap[exterior] || '',
    };
    tempItem.isChecked && appliedFilters.push(tempItem);
    exteriorColor.push(tempItem);
  });

  // Processing odometer
  if (currentFilters.filters?.odometer) {
    const odometer = currentFilters.filters?.odometer || [];
    const { default: odometerDefault } = DEFAULT_MILEAGE_FILTER;
    const { maxLabel, minLabel } = filtersTranslation.mileage.activeFilterLabels;

    odometer.map((ele: any, index: number) => {
      const value =
        index === 0
          ? `${minLabel} ${formatNumberWithCommas(ele, '')}`
          : `${maxLabel} ${formatNumberWithCommas(ele, '')}`;
      if (odometerDefault[index] !== ele) {
        appliedFilters.push({
          key: ele,
          value,
          isChecked: true,
          stock: 0,
          type: 'odometer',
        });
      }
    });
  }

  // Processing price
  if (currentFilters.filters?.price) {
    const price = currentFilters.filters?.price || [];
    const { default: priceDefault, currencySymbol } = DEFAULT_PRICE_FILTER;
    const { maxLabel, minLabel } = filtersTranslation.price.activeFilterLabels;

    price.map((ele: any, index: number) => {
      const value =
        index === 0
          ? `${minLabel} ${formatNumberWithCommas(ele, currencySymbol)}`
          : `${maxLabel} ${formatNumberWithCommas(ele, currencySymbol)}`;
      if (priceDefault[index] !== ele) {
        appliedFilters.push({
          key: ele,
          value,
          isChecked: true,
          stock: 0,
          type: 'price',
        });
      }
    });
  }

  return {
    dealers,
    models,
    years,
    fuelType,
    transmissionType,
    exteriorColor,
    interiorColor,
    appliedFilters,
  };
};

export const removeFilterItem = (
  item: FilterItem,
  filters: UserFilters,
): UserFilters => {
  let newFilters = filters.filters && filters.filters[item.type];

  if (newFilters && (item.type === 'odometer' || item.type === 'price')) {
    const boundaries =
      item.type === 'odometer'
        ? DEFAULT_MILEAGE_FILTER.default
        : DEFAULT_PRICE_FILTER.default;
    const filtersType = filters.filters ? filters.filters[item.type] || [] : [];
    newFilters = filtersType.map((ele: any, index: number) => {
      if (ele === item.key) {
        return boundaries[index];
      }

      return ele;
    });

    return {
      ...filters,
      filters: { ...filters.filters, [item.type]: newFilters },
      wasSearched: false
    };
  }

  if (newFilters && item.type === 'isOnlineShopping') {
    // Set the isOnlineShopping filter to false
    newFilters = false;

    return {
      ...filters,
      filters: { ...filters.filters, [item.type]: newFilters },
      wasSearched: false
    };
  }

  if (newFilters) {
    newFilters = newFilters.filter((filter) => filter !== item.key);

    return {
      ...filters,
      filters: {
        ...filters.filters,
        [item.type]: newFilters
      },
      filterByOnlineBuyingDealers: item.type === 'isOnlineShopping' ? false : filters.filterByOnlineBuyingDealers,
      wasSearched: false
    };
  }

  return filters;
};

export const addFilterItem = (
  item: FilterItem,
  filters: UserFilters,
): UserFilters => {
  let newFilters = (filters && filters.filters && filters.filters[item.type]) || [];

  if (item.type === 'isOnlineShopping') {
    newFilters = true;
  } else {
    newFilters && newFilters?.push(item.key);
  }
  return {
    ...filters,
    filters: { ...filters.filters, [item.type]: newFilters },
    wasSearched: false,
    filterByOnlineBuyingDealers: item.type === 'isOnlineShopping'
  };
};

export const removeAllFilterItems = (
  items: FilterItem[],
  filters: UserFilters,
): UserFilters => {
  if (items.length && items[0]) {
    const filterType = items[0].type;
    return {
      ...filters,
      filters: {
        ...filters.filters,
        [filterType]: []
      }
    };
  }
  return filters;
};

export const addAllFilterItems = (
  items: FilterItem[],
  filters: UserFilters,
): UserFilters => {
  if (items.length && items[0]) {
    const filterType = items[0].type;
    let newFilters: string[] = [];
    items.map((item) => newFilters.push(item.key));
    return {
      ...filters,
      filters: { ...filters.filters, [filterType]: newFilters },
    };
  }
  return filters;
};


export const isOnlineShoppingActive = (
  currentFilters: UserFilters,
): boolean | undefined => {
  return currentFilters?.filters?.isOnlineShopping;
};
